import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Save, RefreshCw, Eye, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SiteSwitcher } from '@/components/admin/SiteSwitcher';
import { SectionEditor } from '@/components/admin/content/SectionEditor';
import { ContentVersionBadge, ContentState } from '@/components/admin/content/ContentVersionBadge';
import { ContentSection } from '@/components/admin/content/RichBlockEditor';
interface DraftContent {
  sections: ContentSection[];
  hero?: {
    headline: string;
    subheadline: string;
    imageUrl?: string;
  };
  seo?: {
    title: string;
    description: string;
  };
}

interface HomepageData {
  id?: string;
  site_id: string;
  draft_content: DraftContent;
  published_content: DraftContent | null;
  content_state: ContentState;
  last_generated_at: string | null;
  published_at: string | null;
}

const defaultDraftContent: DraftContent = {
  sections: [],
  hero: {
    headline: '',
    subheadline: '',
    imageUrl: '',
  },
  seo: {
    title: '',
    description: '',
  },
};

export default function HomepageEdit() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, selectedSite, isGlobalAdmin, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [data, setData] = useState<HomepageData | null>(null);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchHomepageData();
    }
  }, [selectedSiteId]);

  const fetchHomepageData = async () => {
    if (!selectedSiteId) return;
    
    setLoading(true);
    try {
      const { data: hpData, error } = await supabase
        .from('homepage_content')
        .select('*')
        .eq('site_id', selectedSiteId)
        .maybeSingle();

      if (error) throw error;

      let draftContent: DraftContent;
      
      if (hpData) {
        draftContent = (hpData.draft_content as any) || defaultDraftContent;
      } else {
        draftContent = { ...defaultDraftContent };
      }
      
      // Preset SEO title to site name if empty
      if (selectedSite?.name && !draftContent.seo?.title) {
        draftContent.seo = { ...draftContent.seo, title: selectedSite.name };
      }
      
      // Auto-generate SEO description if empty
      const needsSeoDescription = !draftContent.seo?.description;
      
      setData({
        id: hpData?.id,
        site_id: selectedSiteId,
        draft_content: draftContent,
        published_content: hpData?.published_content as any || null,
        content_state: (hpData?.content_state as ContentState) || 'draft',
        last_generated_at: hpData?.last_generated_at || null,
        published_at: hpData?.published_at || null,
      });
      
      // Auto-generate SEO description after setting initial data
      if (needsSeoDescription && selectedSite) {
        autoGenerateSeoDescription();
      }
    } catch (error) {
      console.error('Error fetching homepage data:', error);
      toast.error('Failed to load homepage data');
    } finally {
      setLoading(false);
    }
  };

  const autoGenerateSeoDescription = async () => {
    if (!selectedSiteId || !selectedSite) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data: result, error } = await supabase.functions.invoke('generate-site-description', {
        body: { siteId: selectedSiteId },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (error) {
        console.error('Auto-generate SEO failed:', error);
        return;
      }
      
      if (result?.description) {
        setData(prev => prev ? {
          ...prev,
          draft_content: {
            ...prev.draft_content,
            seo: {
              ...prev.draft_content.seo,
              description: result.description,
            },
            hero: {
              ...prev.draft_content.hero,
              headline: result.hero?.headline || prev.draft_content.hero?.headline || '',
              subheadline: result.hero?.subheadline || prev.draft_content.hero?.subheadline || '',
            },
          }
        } : null);
      }
    } catch (error) {
      console.error('Error auto-generating SEO:', error);
    }
  };

  const handleSave = async () => {
    if (!data || !selectedSiteId) return;
    
    setSaving(true);
    try {
      const upsertData = {
        site_id: selectedSiteId,
        draft_content: data.draft_content as any,
        content_state: 'draft',
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('homepage_content')
        .upsert(upsertData, { onConflict: 'site_id' });

      if (error) throw error;
      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Error saving homepage:', error);
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!data || !selectedSiteId) return;
    
    setPublishing(true);
    try {
      // First save the current draft
      await handleSave();

      // Then promote draft to published
      const { error } = await supabase
        .from('homepage_content')
        .update({
          published_content: data.draft_content as any,
          content_state: 'published',
          published_at: new Date().toISOString(),
        })
        .eq('site_id', selectedSiteId);

      if (error) throw error;

      setData(prev => prev ? {
        ...prev,
        published_content: prev.draft_content,
        content_state: 'published',
        published_at: new Date().toISOString(),
      } : null);

      toast.success('Homepage published successfully');
    } catch (error) {
      console.error('Error publishing homepage:', error);
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleRegenerate = async () => {
    if (!selectedSiteId || !selectedSite) return;
    
    setRegenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { Authorization: `Bearer ${session?.access_token}` };
      
      // Call different functions based on active tab
      if (activeTab === 'sections') {
        // Generate homepage sections
        const { data: result, error } = await supabase.functions.invoke('generate-homepage-sections', {
          body: { siteId: selectedSiteId },
          headers,
        });

        if (error) throw error;
        
        if (result?.sections && Array.isArray(result.sections)) {
          setData(prev => prev ? {
            ...prev,
            draft_content: {
              ...prev.draft_content,
              sections: result.sections,
            }
          } : null);
          toast.success('Content sections regenerated! Save to keep changes.');
        } else {
          toast.info('No sections generated.');
        }
      } else {
        // Generate hero and SEO content
        const { data: result, error } = await supabase.functions.invoke('generate-site-description', {
          body: { siteId: selectedSiteId },
          headers,
        });

        if (error) throw error;
        
        if (result?.description || result?.hero) {
          const updatedDraft = {
            ...data.draft_content,
            seo: {
              ...data.draft_content.seo,
              description: result.description || data.draft_content.seo?.description || '',
            },
            hero: {
              ...data.draft_content.hero,
              headline: result.hero?.headline || data.draft_content.hero?.headline || '',
              subheadline: result.hero?.subheadline || data.draft_content.hero?.subheadline || '',
            },
          };
          
          setData(prev => prev ? { ...prev, draft_content: updatedDraft } : null);
          toast.success('Hero & SEO content regenerated! Save to keep changes.');
        } else {
          toast.info('No new content generated.');
        }
      }
    } catch (error: any) {
      console.error('Error regenerating homepage:', error);
      toast.error(error.message || 'Failed to regenerate content');
    } finally {
      setRegenerating(false);
    }
  };

  const updateDraftContent = (updates: Partial<HomepageData['draft_content']>) => {
    if (!data) return;
    setData({
      ...data,
      draft_content: {
        ...data.draft_content,
        ...updates,
      },
    });
  };

  const hasUnpublishedChanges = data 
    ? JSON.stringify(data.draft_content) !== JSON.stringify(data.published_content)
    : false;

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">No homepage data found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Edit Homepage | Admin</title>
      </Helmet>

      <div className="container max-w-5xl py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-display font-bold">Edit Homepage</h1>
              {selectedSite && (
                <p className="text-sm text-muted-foreground">{selectedSite.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isGlobalAdmin && <SiteSwitcher />}
            <ContentVersionBadge 
              state={data.content_state}
              hasUnpublishedChanges={hasUnpublishedChanges}
              onPublish={handlePublish}
              publishing={publishing}
            />
          </div>
        </div>

        <div className="space-y-6">
          {/* Actions */}
          <div className="flex gap-3 flex-wrap">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Draft
            </Button>
            <Button variant="outline" onClick={handleRegenerate} disabled={regenerating}>
              <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
              Regenerate with AI
            </Button>
            <Button variant="outline" onClick={() => window.open('/', '_blank')}>
              <Eye className="h-4 w-4 mr-2" />
              Preview Site
            </Button>
          </div>

          {/* Content Editor */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="hero">Hero Section</TabsTrigger>
              <TabsTrigger value="sections">Content Sections</TabsTrigger>
              <TabsTrigger value="seo">SEO Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="hero" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Hero Section</CardTitle>
                  <CardDescription>
                    The main headline and introduction at the top of your homepage.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Headline</label>
                    <input
                      type="text"
                      value={data.draft_content.hero?.headline || ''}
                      onChange={(e) => updateDraftContent({
                        hero: { ...data.draft_content.hero, headline: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Your main headline..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Subheadline</label>
                    <textarea
                      value={data.draft_content.hero?.subheadline || ''}
                      onChange={(e) => updateDraftContent({
                        hero: { ...data.draft_content.hero, subheadline: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                      rows={3}
                      placeholder="Supporting text..."
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sections" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Homepage Sections</CardTitle>
                  <CardDescription>
                    Add and edit content sections. Each section can contain headings, paragraphs, images, and videos.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <SectionEditor
                    sections={data.draft_content.sections || []}
                    onChange={(sections) => updateDraftContent({ sections })}
                    sectionLabel="Section"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="seo" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>SEO Settings</CardTitle>
                  <CardDescription>
                    Search engine optimization settings for your homepage.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">SEO Title</label>
                    <input
                      type="text"
                      value={data.draft_content.seo?.title || ''}
                      onChange={(e) => updateDraftContent({
                        seo: { ...data.draft_content.seo, title: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder={selectedSite?.name || "Page title for search engines..."}
                    />
                    <p className="text-xs text-muted-foreground">
                      {data.draft_content.seo?.title?.length || 0}/60 characters
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">SEO Description</label>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleRegenerate} 
                        disabled={regenerating}
                        className="h-7 text-xs"
                      >
                        <RefreshCw className={`h-3 w-3 mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                        Regenerate
                      </Button>
                    </div>
                    <textarea
                      value={data.draft_content.seo?.description || ''}
                      onChange={(e) => updateDraftContent({
                        seo: { ...data.draft_content.seo, description: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                      rows={3}
                      placeholder="Meta description for search results..."
                    />
                    <p className="text-xs text-muted-foreground">
                      {data.draft_content.seo?.description?.length || 0}/160 characters
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Last Modified Info */}
          <div className="text-sm text-muted-foreground text-center pt-4 border-t">
            {data.last_generated_at && (
              <span>Last generated: {new Date(data.last_generated_at).toLocaleString()}</span>
            )}
            {data.published_at && (
              <span className="ml-4">Last published: {new Date(data.published_at).toLocaleString()}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
